/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {getVersion} from '@ion/core/src/environment/version';
import {useExternalTheme} from '@ion/dls/src/theme/useExternalTheme';
import {DLSTheme} from '@ion/dls/src/types/types';
import defaultTheme from './theme';

/**
 * Provides the list of BMW Web Portal internal domains which are to be excluded
 * from the loading of external themes; i.e. the non-white labelling domains.
 */
export const excludedDomains: readonly string[] = [
  'bmwsecurity.lojack.eu',
  'stg.bmwsecurity.lojack.eu',
  'qa.bmwsecurity.lojack.eu',
  'dev.bmwsecurity.lojack.eu'
];

/**
 * Provides an encapsulation of the  BMW Web Portal Application Theme and domain
 * specific constraints. If the current domain is included in the list of excluded
 * domains, the implementation default DLS theme will be used, otherwise, the theme
 * which has been externally defined via a theme.json document will be retrieved.
 *
 * @param   url URL from which the theme is to be retrieved, defaults to `/theme.json`
 * @returns The application theme based on the default the externally defined theme,
 *          and the BMW and DLS default themes. The order of overrides for theming is
 *          as follows: DLS Theme > BMW Theme > External Theme.
 */
export const useApplicationTheme = (): DLSTheme => {
  const theme: DLSTheme = useExternalTheme({
    version  : getVersion(),
    useCache : false,
    excludedDomains
  });
  return {...defaultTheme, ...(theme || {})};
};

export default useApplicationTheme;
